import 'styles/global.css';

import { AppProps } from 'next/app';

import { AlertProvider } from 'components';
import { NavigationProgress } from 'components/navigation-progress';
import { AuthProvider, InitialAuthState, useAuth } from 'context/auth';
import { RegionProvider } from 'context/region';
import { SiteLayoutProvider } from 'context/site-layout';
import { LoaderProvider } from 'hooks/useLoader';
import { SiteLayoutData } from 'lib/site-layout';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect } from 'react';
import { getRedirectOptions, saveRedirectOptions } from 'utils/redirectOptions';

const Redirector = () => {
  const router = useRouter();
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    if (isLoggedIn) {
      const redirectOptions = getRedirectOptions();
      if (redirectOptions) {
        const { url, action } = redirectOptions;
        if (url && url !== 'null') {
          router.push(url);
          saveRedirectOptions({ url: 'null', action: action });
        }
      }
    }
  }, [isLoggedIn, router]);

  return null;
};

type CustomAppProps = AppProps & {
  siteLayout?: SiteLayoutData;
  initialAuth?: InitialAuthState;
};

const MyApp = ({
  Component,
  pageProps,
  siteLayout,
  initialAuth,
}: CustomAppProps) => {
  return (
    <>
      <Script
        id="google-tag-manager"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-M8J9KX27');`,
        }}
      />
      <AuthProvider initialAuth={pageProps.initialAuth ?? initialAuth}>
        <RegionProvider>
          <LoaderProvider>
            <SiteLayoutProvider value={pageProps.siteLayout ?? siteLayout}>
              <AlertProvider>
                <NavigationProgress />
                <Redirector />
                <Component {...pageProps} />
              </AlertProvider>
            </SiteLayoutProvider>
          </LoaderProvider>
        </RegionProvider>
      </AuthProvider>
    </>
  );
};

export default MyApp;
